import {
  AloftArea,
  AloftPrice,
  ManualAdjustmentKey,
  UnitRateKey,
  isDefined,
  orUndefined,
  schAlertType,
  schAloftArea,
  schAloftPrice,
  schPairedCostRouteItem,
} from '@aloft/toolkit-shared';

import { PropertyGridCellKey } from '../../../../components/PropertyGrid/types.ts';
import { CompPropertyDataState, SubjectPropertyDataState } from '../../../../redux/types.ts';
import { calculateUnitRateAdjustment } from '../../../../redux/features/selectors/adjustmentSelectors/utils/calculateUnitRateAdjustment.ts';
import { AlertDefinition } from '../../types.ts';
import { getAlertTriggers } from '../utils/alertTriggers.ts';
import { getComparableDisplayName, getScopedAlertType } from '../utils/compTypeScoping.ts';
import { getSubjectDataForCompType } from '../utils/getSubjectDataForCompType.ts';

const GLA_DEVIATION_THRESHOLD = 0.1;
const SITE_SIZE_DEVIATION_THRESHOLD = 0.2;
const TARGET_CELL_TOOLTIP = 'Adjusted at different unit rate';
const TIPS =
  'Consider applying a uniformed adjustment rate to the Comp, or explain how you derived the unique adjustment rate.';

type FeatureCheck = {
  label: string;
  manualKey: ManualAdjustmentKey;
  unitRateKey: UnitRateKey;
  adjustmentCellKeyId: 'gla' | 'site';
  threshold: number;
  getUnitAmount: (
    editableData: SubjectPropertyDataState['subjectData']['editableData'],
  ) => number | undefined;
};

const glaFeatureCheck: FeatureCheck = {
  label: 'GLA',
  manualKey: 'gla',
  unitRateKey: 'mainDwelling',
  adjustmentCellKeyId: 'gla',
  threshold: GLA_DEVIATION_THRESHOLD,
  getUnitAmount: (editableData) =>
    orUndefined(schAloftArea.parse)(editableData.improvements?.gla)?.toSqFt().unitAmount,
};

const siteSizeFeatureCheck: FeatureCheck = {
  label: 'Site Size',
  manualKey: 'site',
  unitRateKey: 'siteSize',
  adjustmentCellKeyId: 'site',
  threshold: SITE_SIZE_DEVIATION_THRESHOLD,
  getUnitAmount: (editableData) =>
    orUndefined(schAloftArea.parse)(editableData.site?.area)?.toSqFt().unitAmount,
};

const FEATURE_CHECKS: FeatureCheck[] = [glaFeatureCheck, siteSizeFeatureCheck];

const exceedsDeviationThreshold = (
  manualAdjustment: AloftPrice,
  supportedAdjustment: AloftPrice,
  threshold: number,
): boolean => {
  if (supportedAdjustment.equals(AloftPrice.zero)) return false;

  const deviation = manualAdjustment.sub(supportedAdjustment).ratio(supportedAdjustment);
  return isDefined(deviation) && Math.abs(deviation) > threshold;
};

export const inconsistentAdjustmentRate: AlertDefinition = ({
  compSequence,
  fullCompSequence,
  compType,
  manualAdjustments,
  order,
  propertyDataEntities,
  salesComparisonApproach,
  subjectUnitRates,
}) => {
  if (salesComparisonApproach !== 'unit_rate' || !manualAdjustments || !subjectUnitRates) {
    return [];
  }

  const subjectData = (propertyDataEntities.subject as SubjectPropertyDataState | undefined)
    ?.subjectData.editableData;
  if (!subjectData) return [];

  const scopedSubjectData = getSubjectDataForCompType(subjectData, compType);
  const fixRoute =
    schPairedCostRouteItem.enum[order.isInspectifyOrder ? 'adjustments-grid' : 'adjustments'];
  const triggers = getAlertTriggers({ fixRoute, order });

  return compSequence.flatMap((id) => {
    const propertyData = (propertyDataEntities[id] as CompPropertyDataState | undefined)?.compData
      .editableData;
    if (!propertyData) return [];

    const propertyName = getComparableDisplayName(id, fullCompSequence ?? compSequence);

    return FEATURE_CHECKS.flatMap((featureCheck) => {
      const unitRate = subjectUnitRates[featureCheck.unitRateKey];
      if (!unitRate || unitRate.equals(AloftPrice.zero)) return [];

      const manualAdjustment = orUndefined(schAloftPrice.parse)(
        manualAdjustments[id]?.[featureCheck.manualKey],
      );
      if (!manualAdjustment) return [];

      const subjectUnitAmount = featureCheck.getUnitAmount(scopedSubjectData);
      const compUnitAmount = featureCheck.getUnitAmount(propertyData);
      const unitDifference = (subjectUnitAmount ?? 0) - (compUnitAmount ?? 0);
      if (unitDifference === 0) return [];

      const supportedAdjustment = calculateUnitRateAdjustment({
        subjectUnitAmount,
        compUnitAmount,
        unitRate,
      });
      if (!supportedAdjustment || supportedAdjustment.equals(AloftPrice.zero)) return [];

      if (!exceedsDeviationThreshold(manualAdjustment, supportedAdjustment, featureCheck.threshold)) {
        return [];
      }

      const appliedRate = manualAdjustment.div(Math.abs(unitDifference));
      const rateDisplay = appliedRate?.toString(true) ?? '';

      return [
        {
          type: getScopedAlertType(schAlertType.enum.inconsistentAdjustmentRate, compType),
          status: 'active' as const,
          severity: 'error' as const,
          orderType: ['appraisify'] as const,
          message: `${propertyName} ${featureCheck.label} is adjusted at a significantly different unit rate (${rateDisplay}/SF).`,
          propertyID: id,
          ...(compType ? { compType } : {}),
          fixingInfo: {
            tips: TIPS,
            pairedCostRouteItem: fixRoute,
            fixingMethod: ['comment', 'action'] as const,
            commentAlwaysRequired: false,
            targetCellKey: `${id}-${featureCheck.adjustmentCellKeyId}` as PropertyGridCellKey,
            targetCellTooltip: TARGET_CELL_TOOLTIP,
          },
          triggers,
        },
      ];
    });
  });
};
